import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Currency, countryToCurrency, currencySymbol, normalizeCurrency } from "@/lib/currency";

type GeoState = {
  country: string | null;
  currency: Currency;
  symbol: string;
  loading: boolean;
};

let cached: { country: string | null; currency: Currency } | null = null;
let inflight: Promise<{ country: string | null; currency: Currency }> | null = null;

async function fetchGeo(): Promise<{ country: string | null; currency: Currency }> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke("geo-currency", { method: "GET" });
      if (error) throw error;
      const country = typeof data?.country === "string" ? data.country : null;
      const currency = normalizeCurrency(data?.currency);
      // Cross-check: if backend returned country but a different currency, trust the mapping.
      const resolved = country ? countryToCurrency(country) : currency;
      const result = { country, currency: resolved };
      cached = result;
      return result;
    } catch {
      const result = { country: null, currency: "usd" as Currency };
      cached = result;
      return result;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function useGeoCurrency(): GeoState {
  const [state, setState] = useState<GeoState>(() =>
    cached
      ? { country: cached.country, currency: cached.currency, symbol: currencySymbol(cached.currency), loading: false }
      : { country: null, currency: "usd", symbol: "$", loading: true }
  );

  useEffect(() => {
    let alive = true;
    if (cached) return;
    fetchGeo().then((r) => {
      if (!alive) return;
      setState({ country: r.country, currency: r.currency, symbol: currencySymbol(r.currency), loading: false });
    });
    return () => { alive = false; };
  }, []);

  return state;
}
